import axios from 'axios';

// 创建axios实例
const apiClient = axios.create({
  baseURL: process.env.REACT_APP_API_URL || 'http://localhost:5000/api',
  timeout: 10000,
  headers: {
    'Content-Type': 'application/json',
  },
});

// 请求拦截器 - 添加认证token
apiClient.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('cleveroo_token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// 响应拦截器 - 处理401错误
apiClient.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      // Token过期或无效，清除本地存储
      localStorage.removeItem('cleveroo_token');
      localStorage.removeItem('cleveroo_user');
      window.location.reload();
    }
    return Promise.reject(error);
  }
);

// 认证API接口
export interface LoginRequest {
  email: string;
  password: string;
}

export interface LoginResponse {
  user: {
    id: string;
    email: string;
    name: string;
    avatar?: string;
  };
  token: string;
  message: string;
}

export interface RegisterRequest {
  name: string;
  email: string;
  password: string;
}

export interface GoogleAuthResponse {
  user: {
    id: string;
    email: string;
    name: string;
    avatar?: string;
  };
  token: string;
  message: string;
}

// 登录API
export const login = async (data: LoginRequest): Promise<LoginResponse> => {
  try {
    const response = await apiClient.post('/auth/login', data);
    return response.data;
  } catch (error: any) {
    throw new Error(error.response?.data?.message || '登录失败');
  }
};

// 注册API
export const register = async (data: RegisterRequest): Promise<LoginResponse> => {
  try {
    const response = await apiClient.post('/auth/register', data);
    return response.data;
  } catch (error: any) {
    throw new Error(error.response?.data?.message || '注册失败');
  }
};

// Google OAuth登录
export const googleAuth = async (googleToken: string): Promise<GoogleAuthResponse> => {
  try {
    const response = await apiClient.post('/auth/google', { token: googleToken });
    return response.data;
  } catch (error: any) {
    throw new Error(error.response?.data?.message || 'Google登录失败');
  }
};

// 验证token有效性
export const verifyToken = async (): Promise<LoginResponse> => {
  try {
    const response = await apiClient.get('/auth/verify');
    return response.data;
  } catch (error: any) {
    throw new Error('Token验证失败');
  }
};

// 忘记密码
export const forgotPassword = async (email: string): Promise<{ message: string }> => {
  try {
    const response = await apiClient.post('/auth/forgot-password', { email });
    return response.data;
  } catch (error: any) {
    throw new Error(error.response?.data?.message || '发送重置邮件失败');
  }
};

// 重置密码
export const resetPassword = async (token: string, newPassword: string): Promise<{ message: string }> => {
  try {
    const response = await apiClient.post('/auth/reset-password', { 
      token, 
      password: newPassword 
    });
    return response.data;
  } catch (error: any) {
    throw new Error(error.response?.data?.message || '重置密码失败');
  }
};

// 登出API
export const logout = async (): Promise<void> => {
  try {
    await apiClient.post('/auth/logout');
  } catch (error) {
    // 即使API调用失败，也应该清除本地存储
    console.error('登出API调用失败:', error);
  } finally {
    localStorage.removeItem('cleveroo_token');
    localStorage.removeItem('cleveroo_user');
  }
};

export default apiClient; 